import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import IDeal, { DealStatus, IDealsPayload } from '@/finsight/domain/vo/IDeal';
import { useDIContext } from '@/Framework/DI/DIContext';
import DealRepository from '@/finsight/infrastructure/repository/DealRepository';
import { useDealSubscriptions } from '@/finsight/application/Deal/useDealSubscriptions';
import { UserPlanType } from '@/users/domain/UserPlan';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';
import { getDealStatusFilterItems, getFiltersDealStatus, getInitialPayloadState } from './helpers';

interface ISubscription {
  subscriptionId: string,
  subscriptionSubgroupName: string,
  subscriptionName: string,
  isSubscribed: boolean,
  subscriptionRequiredPlan: UserPlanType,
}

export interface IDealsCollection {
  totalCount: number,
  collection: IDeal[],
  subscriptionStatus?: ISubscription | null,
  pageFilters?: IDealsPayload | null,
}

const useDeal = ({
  collection = [],
  totalCount: initialTotalCount,
  subscriptionStatus,
  pageFilters,
} : IDealsCollection) => {
  const { container } = useDIContext();
  const [isFirstFetching, setIsFirstFetching] = useState<boolean>(true);
  const [dealsCollection, setDealsCollection] = useState<IDeal[]>(collection.slice(0, 10));
  const [bufferCollection, setBufferCollection] = useState<IDeal[]>(collection.slice(10));
  const { subscribeSuccessMessage, subscribeButtonText, subscriptions } = useDealSubscriptions(subscriptionStatus);
  const [totalCount, setTotalCount] = useState<number>(initialTotalCount);
  const [isFetching, setIsFetching] = useState(false);
  const [payload, setPayload] = useState<IDealsPayload>(getInitialPayloadState(pageFilters));

  const dealRepository = container.get<DealRepository>(DealRepository);

  const fetchDeals = useCallback(
      async (payload: IDealsPayload) => {
        try {
          setIsFetching(true);
          const response = await dealRepository.getDeals(payload);
          setTotalCount(response?.totalCount);
          if (payload.page === 1) {
            setDealsCollection(response?.collection.slice(0, 10));
            setBufferCollection(response?.collection.slice(10));
          } else {
            setBufferCollection(response.collection);
          }
        } catch (error) {
          AlertManager.error(getErrorMessage(error));
        } finally {
          setIsFetching(false);
        }
      },
    [],
  );

  useEffect(() => {
    if (!isFirstFetching) {
      fetchDeals(payload);
    }
  }, [payload]);

  const loadMore = () => {
    setDealsCollection(dealsCollection.concat(bufferCollection));
    setPayload((prevPayload) => ({
      ...prevPayload,
      perPage: 10,
      page: prevPayload.page === 1 ? 3 : prevPayload.page + 1,
    }));
    setIsFirstFetching(false);
  };

  const applyDealStatus = (_filterName, dealStatus: DealStatus) => {
    setPayload((prevPayload) => ({
      ...prevPayload,
      filters: {
        ...prevPayload.filters,
        includedStatusGroups: getFiltersDealStatus(prevPayload.filters.includedStatusGroups, dealStatus),
      },
      page: 1,
      perPage: 20,
    }));
    setIsFirstFetching(false);
  };

  useEffect(() => {
    setIsFirstFetching(true);
    setDealsCollection(collection.slice(0, 10));
    setBufferCollection(collection.slice(10));
    setPayload(getInitialPayloadState(pageFilters));
  }, [collection]);

  const filterStatusSet = useMemo(() => ({
    dealStatus: {
      items: getDealStatusFilterItems(payload.filters.includedStatusGroups),
      isVisible: true,
    },
  }), [payload.filters.includedStatusGroups]);

  return {
    subscriptions,
    isFetching,
    collection: dealsCollection,
    totalCount,
    subscribeSuccessMessage,
    subscribeButtonText,
    loadMore,
    fetchDeals,
    applyDealStatus,
    filterStatusSet,
    pageFilters,
  };
};

type DealContextType = ReturnType<typeof useDeal>;

export const DealContext = createContext<DealContextType>(null);

export const useDealContext = () => {
  const context = useContext(DealContext);
  if (!context) {
    throw new Error('useDealContext must be used within the DealContext');
  }
  return context;
};

interface IProps {
  deals?: IDealsCollection,
  children: React.ReactNode,
}

const DealContextProvider = ({ children, deals }: IProps) => (
  <DealContext.Provider value={ useDeal(deals) }>
    { children }
  </DealContext.Provider>
);

export default DealContextProvider;
